import constants from '../api/api_urls';
import { rolesWithId } from '../constants/constants';

class AdminAuthService {
  constructor() {
    this.currentUser = null;
    this.token = null;
  }

  // Initialize authentication from storage
  initializeAuth() {
    try {
      const storedUser = sessionStorage.getItem("admin_user");
      const storedToken = sessionStorage.getItem("admin_token");

      if (storedUser && storedToken && storedUser !== "undefined" && storedToken !== "undefined") {
        this.currentUser = JSON.parse(storedUser);
        this.token = storedToken;

        // Clear expired tokens immediately
        if (this.isTokenExpired(this.token)) {
          console.warn("Stored JWT token expired - logging out");
          this.logout();
          return;
        }
      } else {
        // Check localStorage for shared auth
        const sharedUser = localStorage.getItem("awaken_shared_user");
        const sharedToken = localStorage.getItem("awaken_shared_token");
        
        if (sharedUser && sharedToken) {
          this.currentUser = JSON.parse(sharedUser);
          this.token = sharedToken;
          
          if (this.isTokenExpired(this.token)) {
            console.warn("Shared JWT token expired - logging out");
            this.logout();
            return;
          }

          // Sync to sessionStorage
          sessionStorage.setItem("admin_user", sharedUser);
          sessionStorage.setItem("admin_token", sharedToken);
        }
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      this.clearAuth();
    }
  }

  // Login with credentials
  async login(credentials) {
    try {
      const loginUrl = `${constants.baseUrl}/users/login`;
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const result = await response.json();
      console.log('Login response:', result); // Debug log
      
      // Handle different response formats
      if (response.status === 200) {
        // Backend returns: { success: true, token: "...", data: { user: {...} } }
        const isSuccess = result.success;
        const jwtToken = result.token;
        const userData = result.data?.user || result.data;
        
        if (isSuccess && jwtToken) {
          // Validate JWT token
          if (jwtToken && jwtToken.startsWith('eyJ') && this.isTokenExpired(jwtToken)) {
            return { success: false, error: "JWT token expired" };
          }

          this.currentUser = userData;
          this.token = jwtToken;

          // Store in both storage locations
          sessionStorage.setItem("admin_user", JSON.stringify(userData));
          sessionStorage.setItem("admin_token", jwtToken);
          localStorage.setItem("awaken_shared_user", JSON.stringify(userData));
          localStorage.setItem("awaken_shared_token", jwtToken);

          return { success: true, user: userData, token: jwtToken };
        } else {
          return { success: false, error: result.message || "Login failed - no token received" };
        }
      } else {
        return { success: false, error: result.message || "Login failed" };
      }
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: "Network error during login" };
    }
  }

  // Request OTP for admin forgot password (no auth required)
  async forgotPassword(email) {
    try {
      const url = `${constants.baseUrl}/auth/forgot-password`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: (email || "").trim() }),
      });
      const result = await response.json();
      if (response.ok) {
        return { success: true, message: result.message };
      }
      return { success: false, error: result.message || "Failed to send OTP" };
    } catch (error) {
      console.error("Forgot password error:", error);
      return { success: false, error: "Network error. Please try again." };
    }
  }

  // Verify OTP and set new password (no auth required)
  async resetPassword({ email, otp, newPassword }) {
    try {
      const url = `${constants.baseUrl}/auth/reset-password`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: (email || "").trim(),
          otp: String(otp || "").trim(),
          newPassword: newPassword || "",
        }),
      });
      const result = await response.json();
      if (response.ok) {
        return { success: true, message: result.message };
      }
      return { success: false, error: result.message || "Failed to reset password" };
    } catch (error) {
      console.error("Reset password error:", error);
      return { success: false, error: "Network error. Please try again." };
    }
  }

  // Check if user has admin privileges
  isAdminUser(userData = null) {
    const user = userData || this.currentUser;
    if (!user) return false;
    
    // Check if user has admin role
    const userRole = user.role || user.roleId;
    return userRole === rolesWithId.admin;
  }

  // Check if user is an agent/financial planner
  isAgentUser(userData = null) {
    const user = userData || this.currentUser;
    if (!user) return false;
    
    // Check if user has agent role
    const userRole = user.role || user.roleId;
    return userRole === rolesWithId.agent;
  }

  // Get user role
  getUserRole(userData = null) {
    const user = userData || this.currentUser;
    if (!user) return null;
    
    return user.role || user.roleId;
  }

  // Decode JWT token (client-side only)
  decodeJwt(token) {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      
      const payload = parts[1]
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      
      const decoded = JSON.parse(
        decodeURIComponent(
          atob(payload)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        )
      );
      return decoded;
    } catch (e) {
      console.error("Failed to decode JWT:", e);
      return null;
    }
  }

  // Check token expiry using exp claim
  isTokenExpired(token) {
    if (!token) return true;

    const payload = this.decodeJwt(token);
    if (!payload) return true;

    if (!payload.exp) return false;

    return Date.now() / 1000 > payload.exp;
  }

  // Generate cross-domain form link
  generateFormLink(formId, role = null) {
    if (!this.token) {
      console.error("No authentication token available");
      return null;
    }

    const formAppDomain = constants.formUrl;
    const url = new URL(`/form/${formId}`, formAppDomain);
    url.searchParams.set('token', this.token);
    
    if (role) {
      url.searchParams.set('role', role);
    }
    
    return url.toString();
  }

  // Generate form link for Fact Finder with UID parameter
  generateFactFinderFormLink(uid, role = null, agentId = null, awakenCreatedAt = null) {
    if (!this.token) {
      console.error("No authentication token available");
      return null;
    }

    if (!uid) {
      console.error("UID is required for Fact Finder form link");
      return null;
    }

    const formAppDomain = constants.formUrl;
    // Use 'new' as formId and pass UID as query parameter
    const url = new URL('/form/new', formAppDomain);
    url.searchParams.set('token', this.token);
    url.searchParams.set('uid', uid);
    
    if (role) {
      url.searchParams.set('role', role);
    }
    
    // Add agentId if provided (for auto-assignment)
    if (agentId) {
      url.searchParams.set('agentId', agentId);
    }
    
    // Add awakenCreatedAt timestamp (when data was fetched from Fact Finder)
    // This should be used instead of Fact Finder's original createdAt
    if (awakenCreatedAt) {
      url.searchParams.set('awakenCreatedAt', awakenCreatedAt);
    }
    
    return url.toString();
  }

  // Generate report link
  generateReportLink(formId, role = null) {
    if (!this.token) {
      console.error("No authentication token available");
      return null;
    }

    const formAppDomain = constants.formUrl;
    const url = new URL(`/reports/${formId}`, formAppDomain);
    url.searchParams.set('token', this.token);
    
    if (role) {
      url.searchParams.set('role', role);
    }
    
    return url.toString();
  }

  // Generate renewal form link for locked awakens
  // Creates a new form with same data but updated plan year and renewal mode
  generateRenewalFormLink(originalFormId, planYear = null, role = null, agentId = null) {
    if (!this.token) {
      console.error("No authentication token available");
      return null;
    }

    if (!originalFormId) {
      console.error("Original form ID is required for renewal");
      return null;
    }

    const formAppDomain = constants.formUrl;
    // Use the form ID with renewal parameters
    const url = new URL(`/form/${originalFormId}`, formAppDomain);
    url.searchParams.set('token', this.token);
    url.searchParams.set('renewal', 'true'); // Flag to indicate this is a renewal
    
    // Set plan year to current year if not provided
    const currentYear = planYear || new Date().getFullYear();
    url.searchParams.set('planYear', currentYear);
    
    if (role) {
      url.searchParams.set('role', role);
    }
    
    // Add agentId if provided (for auto-assignment)
    if (agentId) {
      url.searchParams.set('agentId', agentId);
    }
    
    return url.toString();
  }

  // Logout user
  logout() {
    console.log('Logging out user...'); // Debug log
    
    this.currentUser = null;
    this.token = null;

    // Clear sessionStorage
    sessionStorage.removeItem("admin_user");
    sessionStorage.removeItem("admin_token");
    
    // Clear localStorage for cross-domain logout
    localStorage.removeItem("awaken_shared_user");
    localStorage.removeItem("awaken_shared_token");
    
    // Clear additional localStorage items that might exist
    localStorage.removeItem("user");
    localStorage.removeItem("agents");
    
    // Dispatch logout event for other tabs/panels
    window.dispatchEvent(new CustomEvent("crossDomainLogout"));
    
    console.log('Logout completed - all storage cleared'); // Debug log
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Get auth token
  getToken() {
    return this.token;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.currentUser && !!this.token && !this.isTokenExpired(this.token);
  }

  // Clear all authentication
  clearAuth() {
    console.log('Clearing all authentication...'); // Debug log
    
    this.currentUser = null;
    this.token = null;
    
    // Clear sessionStorage
    sessionStorage.removeItem("admin_user");
    sessionStorage.removeItem("admin_token");
    
    // Clear localStorage for cross-domain logout
    localStorage.removeItem("awaken_shared_user");
    localStorage.removeItem("awaken_shared_token");
    
    // Clear additional localStorage items that might exist
    localStorage.removeItem("user");
    localStorage.removeItem("agents");
    
    console.log('Authentication cleared - all storage removed'); // Debug log
  }
}

// Create singleton instance
const adminAuthService = new AdminAuthService();

// Initialize on import
adminAuthService.initializeAuth();

export default adminAuthService;
